import { fg_busy, fg_onEmphasis, fg_positive, fg_warning } from "@clockwise/design-system/tokens";
import {
  CheckCircleFilled,
  ClockwiseIntelligenceFilled,
  EmergencyHomeFilled,
} from "@clockwise/icons";
import { CalendarEventSpacingEnum } from "@clockwise/web-commons/src/components/calendar/calendar-event/CalendarEvent";
import classNames from "classnames";
import React, { useRef, useState } from "react";
import { SchedulingOptionsTradeoffs } from "../../chat/ai-chat/utils/types";
import { TradeoffsPopover } from "../../tradeoffs-popover";

export type ProposalOptionCardVariant = "conflict" | "sbm" | "no-conflict";
type Props = {
  text: string;
  variant: ProposalOptionCardVariant;
  spacing: CalendarEventSpacingEnum;
  onClick: () => void;
  emphasized: boolean;
  popoverSide: "left" | "right" | "bottom";
  tradeoffs: SchedulingOptionsTradeoffs;
  attendeeCount: number;
};

const COLORS: { [v in ProposalOptionCardVariant]: string } = {
  conflict: fg_warning,
  sbm: fg_busy,
  "no-conflict": fg_positive,
};

export const ProposalOptionCard = ({
  text,
  variant,
  spacing,
  onClick,
  emphasized,
  popoverSide,
  tradeoffs,
  attendeeCount,
}: Props) => {
  const popoverAnchorRef = useRef<HTMLDivElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const sizeClass = spacing === CalendarEventSpacingEnum.Full ? "cw-w-4 cw-h-4" : "cw-w-3 cw-h-3";
  const colorClass = emphasized ? fg_onEmphasis : COLORS[variant];
  const Icon = () => {
    switch (variant) {
      case "no-conflict":
        return <CheckCircleFilled style={{ fill: colorClass }} className={sizeClass} />;
      case "sbm":
        return <ClockwiseIntelligenceFilled style={{ fill: colorClass }} className={sizeClass} />;
      case "conflict":
        return <EmergencyHomeFilled style={{ fill: colorClass }} className={sizeClass} />;
    }
  };

  const showPopover = emphasized || isHovered;

  return (
    <div
      className={classNames(
        "cw-h-full cw-w-full cw-border-solid cw-border cw-border-transparent",
        {
          "cw-animate-slowPulse": emphasized,
        },
      )}
      onMouseUp={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{ borderColor: colorClass, color: colorClass }}
        className={classNames(
          "cw-h-full cw-w-full cw-border cw-border-dashed cw-px-2 cw-rounded cw-cursor-pointer cw-opacity-100",
          {
            "cw-py-1": spacing === CalendarEventSpacingEnum.Full,
            "cw-py-0": spacing === CalendarEventSpacingEnum.None,
          },
          {
            "cw-bg-neutral-emphasis cw-text-onEmphasis": emphasized,
            "cw-bg-default": !emphasized,
          },
        )}
        ref={popoverAnchorRef}
      >
        <div className="cw-flex cw-justify-between cw-items-center">
          <div className="cw-body-small cw-leading-[13px] cw-text-[12px] cw-font-medium">
            {text}
          </div>
          <Icon />
        </div>
      </div>
      {showPopover && (
        <TradeoffsPopover
          tradeoffs={tradeoffs}
          side={popoverSide}
          totalAttendeeCount={attendeeCount}
          anchorEl={popoverAnchorRef}
          showAttendeeCount
        />
      )}
    </div>
  );
};
